// Save a list of named combobox actions, for future readability
const SelectActions = {
    None: -1,
    Close: 0,
    CloseSelect: 1,
    First: 2,
    Last: 3,
    Next: 4,
    Open: 5,
    PageDown: 6,
    PageUp: 7,
    Previous: 8,
    Select: 9,
    Type: 10,
};

const IdTemplates = {
    input: (idBase) => `${idBase}-input`,
    menu: (idBase) => `${idBase}-menu`,
    item: (idBase, index) => `${idBase}-item-${index}`,
};

/**
 * Helper functions
 */

/**
 * Filter an array of options against an input string
 * @param {string[]} [options] Options to filter
 * @param {string} filter Search string to match to
 * @param {string[]} [exclude] List of options to exclude
 * @returns {string[]} List of options that begin with the filter string
 */
// eslint-disable-next-line default-param-last
function filterOptions(options = [], filter, exclude = []) {
    return options.filter((option) => {
        const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
        return matches && exclude.indexOf(option) < 0;
    });
}

/**
 * Map a key press to a action
 * @param {KeyboardEvent} event Event containing the keys pressed
 * @param {boolean} menuOpen Set to true if the combobox menu is opened
 * @returns {number} Action to perform, value is an enumeration from SelectActions
 */
function getActionFromKey(event, menuOpen) {
    const {
        key, altKey, ctrlKey, metaKey,
    } = event;
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']; // All keys that will do the default open action
    // Handle opening when closed
    if (!menuOpen && openKeys.includes(key)) {
        return SelectActions.Open;
    }

    // Home and end move the selected option when open or closed
    if (key === 'Home') {
        return SelectActions.First;
    }
    if (key === 'End') {
        return SelectActions.Last;
    }

    // Handle typing characters when open or closed
    if (
        key === 'Backspace'
        || key === 'Clear'
        || (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
    ) {
        return SelectActions.Type;
    }

    // Handle keys when open
    if (menuOpen) {
        if (key === 'ArrowUp' && altKey) {
            return SelectActions.CloseSelect;
        } if (key === 'ArrowDown' && !altKey) {
            return SelectActions.Next;
        } if (key === 'ArrowUp') {
            return SelectActions.Previous;
        } if (key === 'PageUp') {
            return SelectActions.PageUp;
        } if (key === 'PageDown') {
            return SelectActions.PageDown;
        } if (key === 'Escape') {
            return SelectActions.Close;
        } if (key === 'Enter' || key === ' ') {
            return SelectActions.CloseSelect;
        }
    }
    return SelectActions.None;
}

/**
 *  Get index of an option from the option list, based on a search string
 *  If the filter is multiple iterations of the same letter (e.g. "aaa"), then
 *  cycle through first-letter matches
 * @param {string[]} options List of options to search through
 * @param {string} filter Search string to match to
 * @param {number} [startIndex] Start index from which the search iteration begins
 * @returns {number} Index of an option from the option list
 */
function getIndexByLetter(options, filter, startIndex = 0) {
    const orderedOptions = [
        ...options.slice(startIndex),
        ...options.slice(0, startIndex),
    ];
    const firstMatch = filterOptions(orderedOptions, filter)[0];
    const allSameLetter = (array) => array.every((letter) => letter === array[0]);

    // First check if there is an exact match for the typed string
    if (firstMatch) {
        return options.indexOf(firstMatch);
    // If the same letter is being repeated, cycle through first-letter matches
    } if (allSameLetter(filter.split(''))) {
        const matches = filterOptions(orderedOptions, filter[0]);
        return options.indexOf(matches[0]);
    }

    // If no matches, return -1
    return -1;
}

/**
 * Get an updated option index after performing an action
 * @param {number} currentIndex Current index selected from the option list
 * @param {number} maxIndex Maximum index possible of the option list
 * @param {number} action Action performed, value is an enumerations from SelectActions
 * @returns {number} New updated index
 */
function getUpdatedIndex(currentIndex, maxIndex, action) {
    const pageSize = 10; // Used for pageup/pagedown

    switch (action) {
        case SelectActions.First:
            return 0;
        case SelectActions.Last:
            return maxIndex;
        case SelectActions.Previous:
            return Math.max(0, currentIndex - 1);
        case SelectActions.Next:
            return Math.min(maxIndex, currentIndex + 1);
        case SelectActions.PageUp:
            return Math.max(0, currentIndex - pageSize);
        case SelectActions.PageDown:
            return Math.min(maxIndex, currentIndex + pageSize);
        default:
            return currentIndex;
    }
}

/**
 * Check if an element is visible in the browser viewport
 * @param {Element} element Element to test visibility on
 * @returns {boolean} True if the element visible
 */
function isElementInView(element) {
    const bounding = element.getBoundingClientRect();

    return (
        bounding.top >= 0
        && bounding.left >= 0
        && bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        && bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

/**
 * Check if an element is currently scrollable
 * @param {*} element Element to test if scrollable
 * @returns {boolean} True if the element is scrollable
 */
function isScrollable(element) {
    return element && element.clientHeight < element.scrollHeight;
}

/**
 * Ensure a given child element is within the parent's visible scroll area
 * If the child is not visible, scroll the parent
 * @param {Element} activeElement Element that should be visible
 * @param {Element} scrollParent Scrollable parent element of the given element
 */
function maintainScrollVisibility(activeElement, scrollParent) {
    const { offsetHeight, offsetTop } = activeElement;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

    if (isAbove) {
        scrollParent.scrollTo(0, offsetTop);
    } else if (isBelow) {
        scrollParent.scrollTo(0, (offsetTop - parentOffsetHeight) + offsetHeight);
    }
}

class mCombobox extends HTMLDivElement {
    constructor() {
        super();
        this.selectEl = null;
        this.selectMutationObserver = null;
        this.idBase = null;
        this.menuOpened = false;
        this.menuFlipped = false;
        this.options = [];
        this.optionStrings = [];
        this.activeIndex = -1;
        this.comboboxEl = null;
        this.comboboxInputEl = null;
        this.comboboxMenuEl = null;
        this.searchString = '';
        this.searchTimeout = null;

        // Check select element
        this.selectEl = this.querySelector('select');
        if (!this.selectEl) {
            throw new Error(`${this.constructor.name}: Missing <select>`);
        }
        if (this.selectEl.type !== 'select-one') {
            throw new Error(`${this.constructor.name}: Only support select-one type <select>`);
        }
        if (!this.selectEl.id) {
            throw new Error(`${this.constructor.name}: Missing id on <select>`);
        }
        this.selectEl.addEventListener('change', this.onSelectChange.bind(this));
        this.selectMutationObserver = new MutationObserver(() => {
            // Rebuild combobox menu if select mutates
            this.setupItems();
        });
        this.selectMutationObserver.observe(this.selectEl, {
            attributes: true,
            childList: true,
            subtree: true,
        });
        this.idBase = this.id;
        if (!this.idBase) {
            this.idBase = this.selectEl.id ? `combobox-${this.selectEl.id}` : 'combobox';
            this.id = this.idBase;
        }

        // Building combobox
        this.comboboxEl = this.createCombobox(this.selectEl.getAttribute('aria-labelledby'));
        this.comboboxInputEl = this.comboboxEl.querySelector('[role=combobox]');
        this.comboboxMenuEl = this.comboboxEl.querySelector('[role=listbox]');
        this.comboboxEl.appendChild(this.comboboxInputEl);
        this.comboboxEl.appendChild(this.comboboxMenuEl);
        this.appendChild(this.comboboxEl);

        // Setup icon
        if ('iconSrc' in this.dataset) {
            this.style.setProperty('--icon-src', `url(${this.dataset.iconSrc})`);
        }

        // Add event listeners
        this.comboboxInputEl.addEventListener('blur', this.onComboboxBlur.bind(this));
        this.comboboxMenuEl.addEventListener('focusout', this.onComboboxBlur.bind(this));
        this.comboboxInputEl.addEventListener('click', this.onComboboxClick.bind(this));
        this.comboboxInputEl.addEventListener('keydown', this.onComboboxKeyDown.bind(this));

        // Get options and create items
        this.setupItems();
    }

    setupItems() {
        const selectOptionsEl = Array.from(this.selectEl.querySelectorAll('option'));
        this.options = [];
        this.optionStrings = [];
        this.activeIndex = this.selectEl.selectedIndex;

        // Clear combobox menu
        while (this.comboboxMenuEl.firstChild) {
            this.comboboxMenuEl.removeChild(this.comboboxMenuEl.firstChild);
        }

        // Setup disabled properties
        const { disabled } = this.selectEl;
        if (disabled) {
            this.comboboxInputEl.setAttribute('aria-disabled', 'true');
            this.comboboxInputEl.tabIndex = -1;
        } else {
            this.comboboxInputEl.removeAttribute('aria-disabled');
            this.comboboxInputEl.tabIndex = 0;
        }

        // Add heading label
        // TODO: handle <optgroup> instead of using a headingLabel attribut
        if ('headingLabel' in this.dataset) {
            const headingEl = document.createElement('li');
            headingEl.className = 'combobox-group-label';
            headingEl.setAttribute('role', 'presentation');
            headingEl.textContent = this.dataset.headingLabel;
            this.comboboxMenuEl.appendChild(headingEl);
        }

        // Add and setup options
        selectOptionsEl.forEach((optionEl, index) => {
            this.options.push(optionEl.value);
            this.optionStrings.push(optionEl.textContent.trim());
            const isHidden = window.getComputedStyle(optionEl).getPropertyValue('display') === 'none';
            const itemEl = this.createComboboxOption(
                optionEl.innerHTML,
                index,
                !!optionEl.disabled,
                isHidden,
            );
            this.comboboxMenuEl.appendChild(itemEl);
        });
        this.selectOption(this.activeIndex);
    }

    selectOption(index) {
        // Update state
        this.activeIndex = index;

        // Update displayed value
        const selected = this.optionStrings[index] || '';
        this.comboboxInputEl.textContent = selected;

        // Update aria-selected
        const items = Array.from(this.querySelectorAll('[role=option]'));
        items.forEach((itemEl) => {
            itemEl.setAttribute('aria-selected', 'false');
        });
        if (items[index]) {
            items[index].setAttribute('aria-selected', 'true');
        }

        // Synchronize select
        if (this.selectEl.selectedIndex !== this.activeIndex) {
            this.selectEl.selectedIndex = this.activeIndex;
            this.selectEl.dispatchEvent(new Event('change'));
        }
    }

    shouldMenuFlip() {
        const menuRect = this.comboboxMenuEl.getBoundingClientRect();
        const bottomDiff = window.innerHeight - menuRect.bottom;
        if (bottomDiff < 0) {
            // Menu don't have enough space below
            const topDiff = this.getBoundingClientRect().y - menuRect.height;
            if (topDiff > 0) {
                // Enough space above: Should flip the menu
                return true;
            }
        }
        return false;
    }

    updateMenuState(open, callFocus = true) {
        if (
            this.menuOpened === open
            || (open && this.comboboxInputEl.getAttribute('aria-disabled') === 'true')
        ) {
            return;
        }

        // Update state
        this.menuOpened = open;

        // Check if menu has enough space
        if (open && this.shouldMenuFlip()) {
            this.classList.add('menu-flipped');
            this.menuFlipped = true;
        } else if (this.menuFlipped) {
            // Menu is closing and was auto flipped ; Restore initial state
            this.classList.remove('menu-flipped');
            this.menuFlipped = false;
        }

        // Update aria-expanded and styles
        this.comboboxInputEl.setAttribute('aria-expanded', `${open}`);
        if (open) {
            this.classList.add('menu-open');
            this.onOptionChange(this.selectEl.selectedIndex);
        } else {
            this.classList.remove('menu-open');
        }

        // Update aria-activedescendant
        const activeId = open ? IdTemplates.item(this.idBase, this.activeIndex) : '';
        this.comboboxInputEl.setAttribute('aria-activedescendant', activeId);

        if (activeId === '' && !isElementInView(this.comboboxInputEl)) {
            this.comboboxInputEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        // Move focus back to combobox, if needed
        if (callFocus) {
            this.comboboxInputEl.focus();
        }
    }

    getSearchString(char) {
        // Reset typing timeout and start new timeout
        // This allows us to make multiple-letter matches, like a native select
        if (typeof this.searchTimeout === 'number') {
            window.clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = window.setTimeout(() => {
            this.searchString = '';
        }, 500);

        // Add most recent letter to saved search string
        this.searchString += char;
        return this.searchString;
    }

    onComboboxBlur(event) {
        // Do nothing if relatedTarget is contained within the menu
        if (this.comboboxMenuEl.contains(event.relatedTarget)) {
            return;
        }

        // Dismiss current option and close
        if (this.menuOpened) {
            this.updateMenuState(false, false);
        }
    }

    onComboboxClick() {
        this.updateMenuState(!this.menuOpened, false);
    }

    onComboboxKeyDown(event) {
        const { key } = event;
        const max = this.options.length - 1;

        const action = getActionFromKey(event, this.menuOpened);
        switch (action) {
            case SelectActions.Last:
            case SelectActions.First:
                this.updateMenuState(true);
            // Intentional fallthrought
            case SelectActions.Next:
            case SelectActions.Previous:
            case SelectActions.PageUp:
            case SelectActions.PageDown:
                event.preventDefault();
                this.onOptionChange(
                    getUpdatedIndex(this.activeIndex, max, action),
                );
                break;
            case SelectActions.CloseSelect:
                event.preventDefault();
                this.selectOption(this.activeIndex);
            // Intentional fallthrought
            case SelectActions.Close:
                event.preventDefault();
                this.updateMenuState(false);
                break;
            case SelectActions.Type:
                this.onComboboxType(key);
                break;
            case SelectActions.Open:
                event.preventDefault();
                this.updateMenuState(true);
                break;
            default:
                break;
        }
    }

    onComboboxType(letter) {
        // Open the listbox if it is closed
        this.updateMenuState(true);

        // Find the index of the first matching option
        const searchString = this.getSearchString(letter);
        const searchIndex = getIndexByLetter(
            this.optionStrings,
            searchString,
            this.activeIndex + 1,
        );

        // If a match was found, go to it
        if (searchIndex >= 0) {
            this.onOptionChange(searchIndex);
        // If no matches, clear the timeout and search string
        } else {
            window.clearTimeout(this.searchTimeout);
            this.searchString = '';
        }
    }

    onOptionChange(index) {
        // Update state
        this.activeIndex = index;

        // Update aria-activedescendant
        this.comboboxInputEl.setAttribute('aria-activedescendant', IdTemplates.item(this.idBase, index));

        // Update active option styles
        const items = Array.from(this.querySelectorAll('[role=option]'));
        items.forEach((itemEl) => {
            itemEl.classList.remove('option-current');
        });
        if (!items[index]) {
            // Index is -1 or not in the menu
            return;
        }

        items[index].classList.add('option-current');

        // Ensure the new option is in view
        if (isScrollable(this.comboboxMenuEl)) {
            maintainScrollVisibility(items[index], this.comboboxMenuEl);
        }

        // Ensure the new options is visible on screen
        // Ensure the new option is in view
        if (!isElementInView(items[index])) {
            items[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    onOptionClick(index) {
        this.onOptionChange(index);
        this.selectOption(index);
        this.updateMenuState(false);
    }

    onOptionMouseEnter(index) {
        if (index !== this.activeIndex) {
            this.onOptionChange(index);
        }
    }

    onSelectChange(event) {
        const { value } = event.target;
        const index = this.options.indexOf(value);

        if (index >= 0 && index !== this.activeIndex) {
            this.onOptionChange(index);
            this.selectOption(index);
        }
    }

    createCombobox(labelId) {
        const inputId = IdTemplates.input(this.idBase);
        const menuId = IdTemplates.menu(this.idBase);

        const rootEl = document.createElement('div');
        rootEl.classList.add('combobox');

        const inputEl = document.createElement('div');
        inputEl.id = inputId;
        inputEl.className = 'combobox-input combobox-common';
        inputEl.setAttribute('role', 'combobox');
        inputEl.setAttribute('aria-expanded', 'false');
        inputEl.setAttribute('aria-controls', menuId);
        inputEl.setAttribute('aria-haspopup', 'listbox');
        if (labelId) {
            inputEl.setAttribute('aria-labelledby', labelId);
        }
        inputEl.setAttribute('tabindex', '0');
        rootEl.appendChild(inputEl);

        const menuEl = document.createElement('ul');
        menuEl.id = menuId;
        menuEl.className = 'combobox-menu';
        menuEl.setAttribute('role', 'listbox');
        if (labelId) {
            menuEl.setAttribute('aria-labelledby', labelId);
        }
        menuEl.setAttribute('tabindex', '-1');
        rootEl.appendChild(menuEl);

        return rootEl;
    }

    createComboboxOption(optionText, index, isDisabled, isHidden) {
        const itemEl = document.createElement('li');
        itemEl.setAttribute('role', 'option');
        itemEl.id = IdTemplates.item(this.idBase, index);
        itemEl.className = 'combobox-option';
        itemEl.setAttribute('aria-selected', 'false');
        itemEl.innerHTML = optionText;
        if (isDisabled) {
            itemEl.setAttribute('aria-disabled', 'true');
        }
        if (isHidden) {
            itemEl.style.setProperty('display', 'none');
        }

        // Add event listeners
        itemEl.addEventListener('click', (event) => {
            event.stopPropagation();
            this.onOptionClick(index);
        });
        itemEl.addEventListener('mouseenter', this.onOptionMouseEnter.bind(this, index));

        return itemEl;
    }
}

customElements.define('m-combobox', mCombobox, {
    extends: 'div',
});
